<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl leading-9 font-medium font-headline antialiased bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
        Sales Team Members
      </h1>
      <FormButton v-if="GlobalPolicy.edit()" :to="{name: 'agent-add', }" prefix-class="fak" icon="fa-plus" color="blue">Add New Sales Team Member</FormButton>
    </header>
    <div class="flex flex-col mt-4 p-4 rounded-lg">
      <div class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6">
        <div class="mb-4 flex-initial">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" color="gray" placeholder="Search..." />
        </div>
        <div class="mb-4 flex-initial">
          <SelectField v-model="query.status" label="Published" :options="statuses" empty="All Statuses"/>
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
          <FormButton @click="reset" color="blue">Clear Filters</FormButton>
        </div>
        <div class="mb-4 flex-initial sm:mt-6 ml-auto">
          <button @click="showColumns = !showColumns" class="w-9 h-9 items-center justify-center bg-transparent hover:bg-bhav-cool-gray-50 text-bhav-blue-200 hover:text-bhav-blue-500 rounded-lg text-lg">
            <p class="relative">
              <span class="fak fa-col-options leading-none"></span>
              <span class="sr-only">Open Table Column Preferences Drawer</span>
            </p>
          </button>
        </div>
      </div>
      <PaginationTable empty-message="There are currently no sales team items." v-if="!isLoading" :legacy="false" :data="filtered" :show-columns="showColumns" @closeDrawer="showColumns = false" :columns="columns" view="agent-list">

        <template #col-name="{ row }">
          <router-link class="block whitespace-nowrap" :to="{ name: 'agent-show', params: { id: row.id } }">
            <div class="flex items-center">
              <div
                class="flex-shrink-0 rounded-full overflow-hidden bg-bhav-blue-400 h-10 w-10 flex items-center justify-center text-white">
                <img v-if="row.photo_url" class="aspect-square object-cover" :src="row.photo_url" alt="">
                <i v-else class="fas fa-user"></i>
              </div>
              <div class="ml-4">
                <div class="text-sm leading-5 font-medium text-bhav-blue-500">
                  {{ row.first_name }} {{ row.last_name }}
                </div>
              </div>
            </div>
          </router-link>
        </template>

        <template #col-email="{ row }">
          <router-link class="block whitespace-nowrap" :to="{ name: 'agent-show', params: { id: row.id } }">
            <span class="text-sm leading-5 font-medium text-bhav-blue-600 hover:text-bhav-blue-700">
              <a v-if="row?.email" :href="`mailto:${row.email}`">{{ row.email }}</a>
            </span>
          </router-link>
        </template>

        <template #col-phone="{ row }">
          <router-link class="block whitespace-nowrap" :to="{ name: 'agent-show', params: { id: row.id } }">
            <span class="text-sm leading-5 font-medium text-bhav-blue-600 hover:text-bhav-blue-700">
              <a v-if="row?.meta?.phone" :href="`tel:${row.meta.phone}`">{{ row.meta.phone }}</a>
            </span>
          </router-link>
        </template>

        <template #col-status="{ row }">
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center">
              <ToggleButton v-if="GlobalPolicy.edit()" :id="`item-${row.id}-status`" @changed="save(row)"
                :checked="row.status == 'active'"
                on-color="green"
                :label="row.status == 'active' ? 'Active' : 'Inactive'" />
              <template v-else>
                <p title="Active" class="text-bhav-blue fas fa-square-check text-xl" v-if="row.status == 'active'"></p>
                <p title="Inactive" class="text-oakwood-gray-400 fas fa-square-x text-xl" v-else></p>
              </template>
            </div>
            <FormButton v-if="GlobalPolicy.edit()" @click="openDelete(row)" prefix-class="fak" icon="fa-trash" color="red" title="Permanently delete this agent"/>
          </div>
        </template>

      </PaginationTable>
    </div>

    <ModalDialog v-if="deleting" :no-padding="true" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-600 font-semibold">Confirm Sales Team Member Deletion</h2>
        </template>
        <template #body>
            <p class="text-bhav-blue-500"><strong>Attention!</strong> You are about to delete <em>{{ agent?.first_name }} {{ agent?.last_name }}</em>.</p>
            <p class="mt-2"><strong>This will detach this person from all collections and <em>permanently</em> delete their record.</strong></p>
            <p class="mt-2">Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="purge" prefix-class="fak" icon="fa-trash" color="red"/>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>

  </div>
  </template>
  
  <script>
  import axios from "axios";
  import LoadingView from '@/components/LoadingView.vue';
  import {ToggleButton, FormButton, SelectField} from '@/components/form';
  import PaginationTable from '@/components/PaginationTable.vue';
    import { agentStatuses } from "@/helpers/globals";
import ModalDialog from "@/components/ModalDialog.vue";
import { GlobalPolicy } from "@/policies/global";
import SearchInput from "@/components/form/SearchInput.vue";

  const query = {
    search: '',
    status: 'active'
  }
  
  export default {
    setup() {
        return {
            GlobalPolicy,
            columns: [
              {
                name: 'name',
                label: 'Name',
                sort: (row) => `${row.first_name} ${row.last_name}`,
                default: true
              },
              {
                name: 'email',
                label: 'Email',
                sort: (row) => row.email || "",
                default: true
              },
              {
                name: 'phone',
                label: 'Phone',
                sort: (row) => row.meta?.phone || "",
                default: true
              },
              {
                name: 'status',
                label: 'Status',
                sort: (row) => row.status,
                default: true
              }
            ]
        }
    },
    data() {
      return {
        agents: [],
        agent: null,
        deleting: false,
        isLoading: true,
        statuses: agentStatuses,
        query: {...query},
        showColumns: false
      }
    },
    methods: {
      index() {
        axios.get(`agents`)
          .then(res => {
            this.agents = res.data;
            this.isLoading = false;
          })
          .catch(e => {
            throw e;
          });
      },
      save(item) {
        this.isLoading = true;
        if(item.status == 'active') {
          axios.delete(`agents/${item.id}`)
            .then(() => {
              this.index();
              this.isLoading = false;
            })
            .catch(err => {
              console.error(err);
            });
        } else {
          axios.patch(`agents/${item.id}/restore`)
            .then(() => {
              this.index();
              this.isLoading = false;
            })
            .catch(err => {
              console.error(err);
            });
        }
      },
      openDelete(item) {
        this.agent = item;
        this.deleting = true;
      },
      cancel() {
        this.agent = null;
        this.deleting = false;
      },
      purge() {
        this.isLoading = true;
        axios.delete(`agents/${this.agent.id}/purge`)
          .then(() => {
            this.index();
            this.isLoading = false;
            this.cancel();
          })
          .catch(err => {
            console.error(err);
          })
      },
      reset() {
        this.query = {...query};
      }
    },
    computed: {
      filtered() {
        let results = this.agents;
        if (this.query.status !== null) {
          results = results.filter(item => this.query.status === item.status);
        }

        if (this.query.search != '') {
          const searchTerm = this.query.search.toLowerCase();
          results = results.filter(item => {
            const firstName = (item.first_name || '').toLowerCase();
            const lastName = (item.last_name || '').toLowerCase();
            const email = (item.email || '').toLowerCase();
            const phone = (item.meta?.phone || '').toLowerCase();

            return firstName.includes(searchTerm) ||
                   lastName.includes(searchTerm) ||
                   email.includes(searchTerm) ||
                   phone.includes(searchTerm);
          });
        }
        return results;
      },
      hasFilters() {
        return JSON.stringify(this.query) != JSON.stringify(query);
      }
    },
    created() {
      this.index();
    },
    components: { LoadingView, ToggleButton, SelectField, FormButton, PaginationTable, ModalDialog, SearchInput },
  }
  </script>